import { useEffect, useState } from "react";
import "../styles/LocationRadiusFilter.css";

const API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;
const RADIUS_OPTIONS = [1, 2, 5, 10, 20, 50];

export function LocationRadiusFilter({ value, onChange }) {
  const [query, setQuery] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const [suppressNextFetch, setSuppressNextFetch] = useState(false);

  const numericRadius = value?.radiusKm != null ? Number(value.radiusKm) : 10;
  const radiusKm = Number.isNaN(numericRadius) ? 10 : numericRadius;

  useEffect(() => {
    const nextLabel = value?.label || "";
    setQuery(nextLabel);

    if (nextLabel) {
      setSuppressNextFetch(true);
      setResults([]);
      setIsOpen(false);
    }
  }, [value?.label]);

  useEffect(() => {
    if (suppressNextFetch) {
      setSuppressNextFetch(false);
      return;
    }

    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
            query
          )}&lang=pl&filter=countrycode:pl&apiKey=${API_KEY}`,
          { signal: controller.signal }
        );
        const data = await res.json();
        setResults(data.features || []);
        setIsOpen(true);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Geoapify error", err);
        }
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [query]);

  const handleSelect = (feature) => {
    if (!feature.geometry || !feature.geometry.coordinates) return;
    const [lng, lat] = feature.geometry.coordinates;

    const label =
      feature.properties?.formatted ||
      feature.properties?.city ||
      feature.properties?.name ||
      query;

    onChange({
      lat,
      lng,
      radiusKm,
      label,
    });

    setSuppressNextFetch(true);
    setQuery(label);
    setResults([]);
    setIsOpen(false);
  };

  const handleRadiusSelectChange = (e) => {
    const nextRadius = Number(e.target.value) || radiusKm;

    onChange({
      lat: value?.lat || null,
      lng: value?.lng || null,
      radiusKm: nextRadius,
      label: value?.label || query || "",
    });
  };

  const handleBlur = () => {
    // małe opóźnienie, żeby klik w sugestię zdążył wejść
    setTimeout(() => setIsOpen(false), 150);
  };

  const handleUseMyLocation = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      console.warn("Geolokalizacja nie jest wspierana w tej przeglądarce");
      return;
    }

    setIsGeoLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;

        let label = "Moja lokalizacja";

        try {
          const res = await fetch(
            `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&lang=pl&apiKey=${API_KEY}`
          );
          if (res.ok) {
            const data = await res.json();
            const props = data.features?.[0]?.properties;

            label =
              props?.formatted ||
              [props?.city, props?.country].filter(Boolean).join(", ") ||
              props?.city ||
              "Twoja lokalizacja";
          }
        } catch (err) {
          console.error("Reverse geocoding error:", err);
        }

        onChange({
          lat: latitude,
          lng: longitude,
          radiusKm,
          label,
        });

        setSuppressNextFetch(true);
        setQuery(label);
        setResults([]);
        setIsOpen(false);
        setIsGeoLoading(false);
      },
      (error) => {
        console.error("Błąd geolokalizacji:", error);
        setIsGeoLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 300000,
      }
    );
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="location-filter">
      <div className="location-filter__row">
        {/* Lokalizacja */}
        <div className="location-filter__field location-filter__field--grow">
          <div className="location-filter__label-row">
            {/* <span className="location-filter__label">Lokalizacja</span> */}
          </div>
          <div className="location-filter__input-wrapper">
            <input
              type="text"
              className="location-filter__input"
              placeholder="Lokalizacja"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleBlur}
            />
            {(isLoading || isGeoLoading) && (
              <div className="location-filter__spinner" aria-hidden="true" />
            )}
          </div>

          {isOpen && (
            <ul className="location-filter__suggestions">
              <li
                className="location-filter__suggestion-item location-filter__suggestion-item--geo"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleUseMyLocation();
                }}
              >
                <i className="bi bi-geo-alt-fill me-1" />
                {isGeoLoading
                  ? "Pobieranie lokalizacji..."
                  : "Użyj mojej lokalizacji"}
              </li>

              {results.map((feature) => {
                const key =
                  feature.properties?.place_id ||
                  feature.properties?.formatted;
                const label =
                  feature.properties?.formatted ||
                  feature.properties?.city ||
                  feature.properties?.name;

                return (
                  <li
                    key={key}
                    className="location-filter__suggestion-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(feature);
                    }}
                  >
                    {label}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Lista promieni */}
        <div className="location-filter__field location-filter__field--radius">
          <div className="location-filter__label-row">
            {/* <span className="location-filter__label">Promień</span> */}
          </div>
          <div className="location-filter__radius-select-wrapper">
            <select
              className="location-filter__radius-select"
              value={radiusKm}
              onChange={handleRadiusSelectChange}
            >
              {RADIUS_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r} km
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
